import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardIcon,
  TicketIcon
} from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import LoadingSpinner from '../components/LoadingSpinner';
import useApi from '../hooks/useApi';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const CLIENT_URL = process.env.REACT_APP_CLIENT_URL || 'http://localhost:3000';

function Affiliates() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState(undefined);
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const limit = 20;

  // Fetch affiliates
  const { data, loading, error, refetch } = useApi(
    `/api/admin/affiliates?search=${search}&isActive=${isActiveFilter !== undefined ? isActiveFilter : ''}&page=${page}&limit=${limit}`,
    [search, isActiveFilter, page]
  );

  const affiliates = data?.data?.affiliates || [];
  const totalPages = data?.data?.totalPages || 1;
  const total = data?.data?.total || 0;
  const stats = data?.data?.stats || {
    activeAffiliates: 0,
    totalClicks: 0,
    totalEarnings: 0
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (value) => {
    setIsActiveFilter(value === 'all' ? undefined : value === 'active');
    setPage(1);
  };

  const copyAffiliateLink = (code) => {
    const link = `${CLIENT_URL}?ref=${code}`;
    navigator.clipboard.writeText(link);
    toast.success('Affiliate link copied to clipboard!');
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this affiliate?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/admin/affiliates/${id}`);
      toast.success('Affiliate deactivated successfully');
      refetch();
    } catch (error) {
      console.error('Error deactivating affiliate:', error);
      toast.error('Failed to deactivate affiliate');
    }
  };

  const columns = [
    {
      key: 'code',
      label: 'Code',
      render: (affiliate) => (
        <div className="flex items-center space-x-2">
          <span className="font-mono font-semibold text-purple-600">{affiliate.code}</span>
          <button
            onClick={() => copyAffiliateLink(affiliate.code)}
            className="text-gray-400 hover:text-purple-600 transition-colors"
            title="Copy affiliate link"
          >
            <ClipboardIcon className="h-4 w-4" />
          </button>
        </div>
      )
    },
    {
      key: 'name',
      label: 'Name',
      render: (affiliate) => (
        <div>
          <div className="font-medium text-gray-900">{affiliate.name}</div>
          <div className="text-sm text-gray-500">{affiliate.email}</div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (affiliate) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          affiliate.is_active
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {affiliate.is_active ? (
            <>
              <CheckCircleIcon className="h-3 w-3 mr-1" />
              Active
            </>
          ) : (
            <>
              <XCircleIcon className="h-3 w-3 mr-1" />
              Inactive
            </>
          )}
        </span>
      )
    },
    {
      key: 'total_clicks',
      label: 'Clicks',
      render: (affiliate) => (
        <span className="font-semibold text-gray-900">{affiliate.total_clicks || 0}</span>
      )
    },
    {
      key: 'total_conversions',
      label: 'Conversions',
      render: (affiliate) => (
        <span className="font-semibold text-green-600">{affiliate.total_conversions || 0}</span>
      )
    },
    {
      key: 'total_commissions_earned',
      label: 'Earnings',
      render: (affiliate) => (
        <span className="font-semibold text-purple-600">
          ${parseFloat(affiliate.total_commissions_earned || 0).toFixed(2)}
        </span>
      )
    },
    {
      key: 'discount',
      label: 'Discount',
      render: (affiliate) => affiliate.discount_coupon ? (
        <div className="flex items-center">
          <TicketIcon className="h-4 w-4 text-green-600 mr-1" />
          <span className="text-sm text-green-600 font-medium">
            {affiliate.discount_type === 'fixed' 
              ? (affiliate.discount_amount ? `$${parseFloat(affiliate.discount_amount).toFixed(2)}` : 'Active')
              : (affiliate.discount_percent ? `${affiliate.discount_percent}%` : 'Active')
            }
          </span>
        </div>
      ) : (
        <span className="text-gray-400 text-sm">None</span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (affiliate) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/affiliates/${affiliate.id}`)}
            className="text-purple-600 hover:text-purple-900 text-sm font-medium"
          >
            View Details
          </button>
          {affiliate.is_active && (
            <button
              onClick={() => handleDeactivate(affiliate.id)}
              className="text-red-600 hover:text-red-900 text-sm font-medium"
            >
              Deactivate
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <UserGroupIcon className="h-8 w-8 mr-3 text-purple-600" />
              Affiliates
            </h1>
            <p className="mt-2 text-gray-600">
              Manage affiliate partners and track referral performance
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Affiliate
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500">Total Affiliates</div>
            <div className="mt-2 text-3xl font-bold text-gray-900">{total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500">Active Affiliates</div>
            <div className="mt-2 text-3xl font-bold text-green-600">
              {stats.activeAffiliates}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500">Total Clicks</div>
            <div className="mt-2 text-3xl font-bold text-blue-600">
              {stats.totalClicks.toLocaleString()}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500">Total Earnings</div>
            <div className="mt-2 text-3xl font-bold text-purple-600">
              ${stats.totalEarnings.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, or code..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Status Filter */}
              <select
                value={isActiveFilter === undefined ? 'all' : isActiveFilter ? 'active' : 'inactive'}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="large" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">
              Error loading affiliates: {error.message}
            </div>
          ) : affiliates.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No affiliates found
            </div>
          ) : (
            <>
              <Table columns={columns} data={affiliates} />
              
              {totalPages > 1 && (
                <div className="p-6 border-t">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Affiliate Modal */}
      {showCreateModal && (
        <CreateAffiliateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
            toast.success('Affiliate created successfully');
          }}
        />
      )}
    </AdminLayout>
  );
}

// Create Affiliate Modal Component
function CreateAffiliateModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    email: '',
    phone: '',
    notes: '',
    discountCoupon: '',
    discountType: 'percent',
    discountPercent: '',
    discountAmount: '',
    discountDescription: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDiscountSection, setShowDiscountSection] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        discountCoupon: formData.discountCoupon || null,
        discountType: formData.discountType,
        discountPercent: formData.discountPercent ? parseFloat(formData.discountPercent) : 0,
        discountAmount: formData.discountAmount ? parseFloat(formData.discountAmount) : 0,
        discountDescription: formData.discountDescription || null
      };
      await axios.post(`${API_BASE_URL}/api/admin/affiliates`, submitData);
      onSuccess();
    } catch (error) {
      console.error('Error creating affiliate:', error);
      setError(error.response?.data?.error || 'Failed to create affiliate');
      setLoading(false);
    }
  };

  const generateCode = () => {
    if (formData.name) {
      const cleanName = formData.name.toUpperCase().replace(/[^A-Z0-9]/g, '');
      const year = new Date().getFullYear();
      const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
      setFormData(prev => ({
        ...prev,
        code: `${cleanName.substring(0, 6)}${year}${random}`
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Create New Affiliate</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code *
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                  pattern="[A-Z0-9]+"
                  title="Alphanumeric characters only"
                />
                <button
                  type="button"
                  onClick={generateCode}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Generate
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">Alphanumeric characters only</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Discount Coupon Section */}
          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setShowDiscountSection(!showDiscountSection)}
              className="flex items-center text-sm font-medium text-purple-600 hover:text-purple-700"
            >
              <TicketIcon className="h-5 w-5 mr-2" />
              {showDiscountSection ? 'Hide Discount Settings' : 'Add Discount Coupon (Optional)'}
            </button>

            {showDiscountSection && (
              <div className="mt-4 space-y-4 p-4 bg-purple-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    iCount Coupon Code
                  </label>
                  <input
                    type="text"
                    value={formData.discountCoupon}
                    onChange={(e) => setFormData(prev => ({ ...prev, discountCoupon: e.target.value }))}
                    placeholder="Enter the coupon code from iCount"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Type
                  </label>
                  <div className="flex space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="createDiscountType"
                        value="percent"
                        checked={formData.discountType === 'percent'}
                        onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                        className="form-radio text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-gray-700">Percentage (%)</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="createDiscountType"
                        value="fixed"
                        checked={formData.discountType === 'fixed'}
                        onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                        className="form-radio text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-gray-700">Fixed Amount ($)</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.discountType === 'percent' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Percentage
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={formData.discountPercent}
                          onChange={(e) => setFormData(prev => ({ ...prev, discountPercent: e.target.value }))}
                          placeholder="e.g., 10"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-8"
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.discountAmount}
                          onChange={(e) => setFormData(prev => ({ ...prev, discountAmount: e.target.value }))}
                          placeholder="e.g., 25.00"
                          className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.discountDescription}
                      onChange={(e) => setFormData(prev => ({ ...prev, discountDescription: e.target.value }))}
                      placeholder="e.g., $25 off first month"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Affiliate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Affiliates;

